import { Eye, EyeOff, Filter, GripVertical, Lock, LockOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { specFor } from "./catalog";
import { isBarWidget, type SPage, type SWidget } from "./model";
import { stackOrder } from "./zorder";

/** Layers pane: every visual on the page, front (top) to back (bottom). */
export function LayersPanel({
  page,
  selectedIds,
  renameId,
  onRenameDone,
  onSelect,
  onPatch,
  onMove,
}: {
  page: SPage;
  selectedIds: string[];
  /** Start renaming this visual (F2). */
  renameId: string | null;
  onRenameDone: () => void;
  /** Plain click replaces the selection; Shift/Ctrl-click toggles. */
  onSelect: (id: string, toggle: boolean) => void;
  onPatch: (id: string, patch: Partial<SWidget>, key?: string) => void;
  /** Drag-and-drop: put `id` in front of / behind `targetId`. */
  onMove: (id: string, targetId: string, where: "front" | "behind") => void;
}) {
  const byId = new Map(page.widgets.map((w) => [w.id, w]));
  const frontToBack = stackOrder(page).reverse();
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; where: "front" | "behind" } | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const labelOf = (w: SWidget) => w.title || specFor(w.type)?.label || w.type;
  const begin = (w: SWidget) => {
    setEditing(w.id);
    setDraft(w.title ?? "");
  };
  const finish = (save: boolean) => {
    const id = editing;
    setEditing(null);
    onRenameDone();
    if (save && id) {
      const w = byId.get(id);
      if (w && (w.title ?? "") !== draft) onPatch(id, { title: draft.trim() }, `layer-title-${id}`);
    }
  };

  useEffect(() => {
    if (!renameId) return;
    const w = byId.get(renameId);
    if (w) begin(w);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameId]);

  // Keep the canvas selection visible in the list.
  const primary = selectedIds[0];
  useEffect(() => {
    if (primary) rowRefs.current[primary]?.scrollIntoView({ block: "nearest" });
  }, [primary]);

  if (page.widgets.length === 0) {
    return (
      <div className="st-pane">
        <div className="ds-small ds-faint">No visuals on this page yet. Add one from the Visuals tab.</div>
      </div>
    );
  }

  return (
    <div className="st-pane st-layers" aria-label="Layers">
      <div className="ds-small ds-faint" style={{ marginBottom: 8 }}>Top = in front. Drag to re-stack. Click to select, Shift-click for several, F2 or double-click to rename.</div>
      {frontToBack.map((id) => {
        const w = byId.get(id);
        if (!w) return null;
        const spec = specFor(w.type);
        const Icon = spec?.icon;
        const sel = selectedIds.includes(id);
        const bar = isBarWidget(w);
        const drop = over?.id === id && dragId !== id ? over.where : null;
        return (
          <div
            key={id}
            ref={(el) => { rowRefs.current[id] = el; }}
            className={`st-layer${sel ? " is-selected" : ""}${w.hidden ? " is-hidden" : ""}${drop ? ` is-drop-${drop}` : ""}`}
            role="option"
            aria-selected={sel}
            tabIndex={0}
            draggable={editing !== id}
            onDragStart={(e) => {
              setDragId(id);
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("application/x-mrp-layer", id);
            }}
            onDragOver={(e) => {
              if (!dragId) return;
              e.preventDefault();
              const r = e.currentTarget.getBoundingClientRect();
              const where = e.clientY < r.top + r.height / 2 ? "front" : "behind";
              setOver((o) => (o && o.id === id && o.where === where ? o : { id, where }));
            }}
            onDragLeave={() => setOver((o) => (o?.id === id ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId && over) onMove(dragId, over.id, over.where);
              setDragId(null);
              setOver(null);
            }}
            onDragEnd={() => {
              setDragId(null);
              setOver(null);
            }}
            onClick={(e) => onSelect(id, e.shiftKey || e.ctrlKey || e.metaKey)}
            onDoubleClick={() => begin(w)}
            onKeyDown={(e) => {
              if (editing === id) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(id, e.shiftKey || e.ctrlKey || e.metaKey);
              } else if (e.key === "F2") {
                e.preventDefault();
                e.stopPropagation();
                begin(w);
              }
            }}
            title={`${labelOf(w)}${bar ? " (filter bar)" : ""}`}
          >
            <GripVertical size={12} className="st-layer__grip" aria-hidden="true" />
            <span className="st-layer__icon">{Icon ? <Icon size={14} /> : null}</span>
            {editing === id ? (
              <input
                className="st-layer__edit"
                autoFocus
                value={draft}
                placeholder={spec?.label ?? w.type}
                aria-label="Visual name"
                onChange={(e) => setDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onBlur={() => finish(true)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") finish(true);
                  else if (e.key === "Escape") finish(false);
                }}
              />
            ) : (
              <span className="st-layer__name ds-truncate">{labelOf(w)}</span>
            )}
            {bar ? <span className="st-layer__badge" title="Shown in the filter bar above the canvas"><Filter size={10} /> bar</span> : null}
            <button
              type="button"
              className={`st-layer__btn${w.locked ? " is-on" : ""}`}
              aria-label={w.locked ? "Unlock" : "Lock"}
              aria-pressed={!!w.locked}
              title={w.locked ? "Unlock (can be moved and deleted again)" : "Lock (cannot be moved, resized or deleted by accident)"}
              onClick={(e) => { e.stopPropagation(); onPatch(id, { locked: w.locked ? false : true }); }}
            >
              {w.locked ? <Lock size={13} /> : <LockOpen size={13} />}
            </button>
            <button
              type="button"
              className={`st-layer__btn${w.hidden ? " is-on" : ""}`}
              aria-label={w.hidden ? "Show" : "Hide"}
              aria-pressed={!!w.hidden}
              title={w.hidden ? "Hidden from viewers and TVs. Click to show." : "Hide from viewers and TVs (stays here in the designer)"}
              onClick={(e) => { e.stopPropagation(); onPatch(id, { hidden: w.hidden ? false : true }); }}
            >
              {w.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
            </button>
          </div>
        );
      })}
    </div>
  );
}
